import { useDraggable } from "@dnd-kit/core";
import { BotIcon, BotMessageSquareIcon, UserRoundIcon } from "lucide-react";

import { KanbanOwner } from "./KanbanOwner";
import { StevieCallIndicators } from "@jobs/pipeline/shared/StevieCallIndicators";
import { AgenticScoreChip } from "@jobs/pipeline/shared/AgenticScoreChip";
import { CandidacyQuickActions } from "@jobs/pipeline/shared/CandidacyQuickActions";
import { CandidacyStageChip } from "@jobs/pipeline/shared/CandidacyStageChip";
import { LastPingDisplay } from "@jobs/pipeline/shared/LastPingDisplay";
import { LocationAndCommute } from "@jobs/pipeline/shared/LocationAndCommute";
import { TimeInStageDisplay } from "@jobs/pipeline/shared/TimeInStageDisplay";
import { Column, Row, Text } from "@/components";
import { InternalSourceBadge } from "@/components/InternalSourceBadge";
import { Card } from "@/components/ui/card";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip";

import { useTaggableUsers } from "@/contexts/TaggableUsersContext";
import { agenticScoreToBucket } from "@/lib/agenticScore";
import { dates } from "@/lib/dates";
import {
  hiddenPipelineCandidacyStatuses,
  stageChipStatusesByStage,
} from "@/lib/constants";
import { formatting } from "@/lib/formatting";
import { misc } from "@/lib/misc";
import { stevieRoute } from "@/lib/stevieRoute";
import { cn } from "@/lib/utils";
import {
  CandidacyStatus,
  MinimalCandidacy,
  PipelineStage,
  PipelineViewPreference,
} from "@/types";

const TOOLTIP_DELAY_MS = 100;

export const KanbanCandidacyCard = ({
  candidacy,
  isAnyDragging = false,
  isDragOverlay = false,
  stage,
  onClick,
  onOpenMessages,
  onRequestStatusChangeDialog,
}: {
  candidacy: MinimalCandidacy;
  isAnyDragging?: boolean;
  isDragOverlay?: boolean;
  stage: PipelineStage;
  onClick: (candidacy: MinimalCandidacy) => void;
  onOpenMessages: (candidacy: MinimalCandidacy) => void;
  onRequestStatusChangeDialog: (
    candidacyId: string,
    status: CandidacyStatus,
  ) => void;
}) => {
  const { userInitialsMap } = useTaggableUsers();
  const fullName = misc.toTitleCase(
    formatting.fullName(candidacy.firstName, candidacy.lastName, "Unknown"),
  );
  const agenticRating = agenticScoreToBucket(
    candidacy.agenticScores[0]?.toScore,
  );
  const ownerUserIds = candidacy.owners.map((o) => o.userId);
  const isHidden = hiddenPipelineCandidacyStatuses.includes(
    candidacy.lastCandidacyStatusChange.toStatus,
  );
  const isInCommunication = stage === PipelineStage.IN_COMMUNICATION;
  const isAgentLed = isInCommunication && candidacy.stafferAgentActive;
  const isManualLed = isInCommunication && !candidacy.stafferAgentActive;
  // Shortlisted by a Stevie route: Stevie's blue instead of the human marker.
  const isStevieShortlisted =
    isManualLed && stevieRoute.isShortlistedViaStevie(candidacy);
  // Routed to Stevie and still waiting on the call: muted, out of the queue.
  const isAwaitingStevie =
    stage === PipelineStage.APPLIED && stevieRoute.isAwaitingCall(candidacy);
  // Rejected/closed, employed, agent-led and Stevie-routed cards can't be
  // dragged. Employed is terminal — rolling a placement back from there isn't
  // supported.
  const isDraggable =
    !isDragOverlay &&
    !isHidden &&
    !isAgentLed &&
    !isAwaitingStevie &&
    candidacy.lastCandidacyStatusChange.toStatus !== CandidacyStatus.EMPLOYED;

  /* React Hooks */
  const { listeners, setNodeRef } = useDraggable({
    data: { candidacy, stage },
    disabled: !isDraggable,
    id: candidacy.id,
  });

  /* Handlers */
  const handleClick = () => {
    onClick(candidacy);
  };

  /* Render */
  const renderCommunicationModeIcon = () => {
    if (!isInCommunication) return null;
    if (isAgentLed) {
      if (!candidacy.nextScheduledAgentMessageAt) {
        return (
          <BotMessageSquareIcon className="h-3.5 w-3.5 shrink-0 text-slate-500" />
        );
      }
      return (
        <TooltipProvider delayDuration={TOOLTIP_DELAY_MS}>
          <Tooltip>
            <TooltipTrigger asChild>
              <BotMessageSquareIcon className="h-3.5 w-3.5 shrink-0 cursor-default text-slate-500" />
            </TooltipTrigger>
            <TooltipContent className="px-2 py-1" side="top">
              <Column className="gap-0.5">
                <Text className="text-xs font-semibold text-slate-700">
                  Scheduled message
                </Text>
                <Text className="text-xs font-normal text-slate-500">
                  Sends at{" "}
                  {dates.formatTime(candidacy.nextScheduledAgentMessageAt)}
                </Text>
              </Column>
            </TooltipContent>
          </Tooltip>
        </TooltipProvider>
      );
    }
    if (isStevieShortlisted) {
      return <BotIcon className="h-3.5 w-3.5 shrink-0 text-primary-700" />;
    }
    return <UserRoundIcon className="h-3.5 w-3.5 shrink-0 text-primary-700" />;
  };

  const render = () => (
    <Column ref={setNodeRef} {...(isDraggable ? listeners : {})}>
      <Card
        className={cn(
          "group relative min-w-[300px] cursor-default px-2.5 py-2 transition-colors hover:border-primary-300 hover:shadow-md",
          // A card under the cursor mid-drag must not swallow the drop or fire a click.
          isAnyDragging && "pointer-events-none",
          isDragOverlay && "opacity-60 shadow-lg",
          isHidden &&
            "border-red-200 bg-red-50 hover:border-red-300 dark:border-[#783129] dark:bg-[#602A24] dark:hover:border-[#913D32]",
          !isHidden &&
            isManualLed &&
            !isStevieShortlisted &&
            "border-t-4 border-t-primary-600 shadow-md dark:border-t-[#6F8FAA]",
          !isHidden &&
            isStevieShortlisted &&
            "border-t-4 border-t-[#ACD1F5] shadow-md dark:border-t-[#C6D6E2]",
          !isHidden && isAgentLed && "border-t-4 border-t-slate-300",
          isAwaitingStevie && "opacity-70",
        )}
        onClick={handleClick}
      >
        <Column className="gap-1.5">
          <Row className="items-start justify-between gap-2">
            <Column className="min-w-0 gap-0.5">
              <Row className="min-w-0 items-center gap-1.5">
                <Text className="truncate text-sm font-semibold text-slate-900">
                  {fullName}
                </Text>
                {renderCommunicationModeIcon()}
                <InternalSourceBadge source={candidacy.source} isIconOnly />
              </Row>
              <LocationAndCommute
                commuteInfo={candidacy.commuteInfo}
                location={candidacy.location}
                variant="xs"
              />
            </Column>
            <Row className="shrink-0 items-center gap-1.5">
              <StevieCallIndicators
                currentScore={candidacy.agenticScores[0]?.toScore ?? null}
                elevatedFrom={candidacy.elevatedFrom}
                stevieCallDuration={candidacy.stevieCallDuration}
              />
              <KanbanOwner
                ownerUserIds={ownerUserIds}
                userInitialsMap={userInitialsMap}
              />
              {agenticRating ? (
                <AgenticScoreChip
                  bucket={agenticRating}
                  isHumanRated={candidacy.agenticScores[0]?.userId != null}
                  justification={candidacy.agenticScores[0]?.justification}
                />
              ) : null}
            </Row>
          </Row>
          {stage === PipelineStage.APPLIED ||
          stageChipStatusesByStage[stage] ? (
            <Row>
              <CandidacyStageChip
                candidacy={candidacy}
                disabled={isAgentLed}
                onRequestStatusChangeDialog={onRequestStatusChangeDialog}
                stage={stage}
              />
            </Row>
          ) : null}
          <Row className="items-center justify-between gap-2">
            <Row className="items-center gap-2">
              <TimeInStageDisplay candidacy={candidacy} showTooltip />
              {stage !== PipelineStage.APPLIED && (
                <LastPingDisplay
                  candidacy={candidacy}
                  showTooltip
                  stage={stage}
                />
              )}
            </Row>
            {stage !== PipelineStage.APPLIED && (
              <CandidacyQuickActions
                candidacy={candidacy}
                onOpenMessages={onOpenMessages}
                pipelineView={PipelineViewPreference.KANBAN}
              />
            )}
          </Row>
        </Column>
      </Card>
    </Column>
  );

  return render();
};
